import { forwardRef, type InputHTMLAttributes, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils/cn';
import { spring } from '@/lib/motion/presets';

interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** When true, render with a glowing accent border. */
  focused?: boolean;
  /** Visual error state. */
  invalid?: boolean;
}

/**
 * PasswordInput — monospace input with a large dot count preview and an
 * inline reveal toggle. Designed for the lock screen first; reusable
 * elsewhere with the same props.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, focused, invalid, disabled, value, ...rest }, ref) => {
    const [revealed, setRevealed] = useState(false);
    const charCount = typeof value === 'string' ? value.length : 0;
    const showDots = !revealed && charCount > 0;

    return (
      <div
        className={cn(
          'group relative w-full rounded-md border bg-surface transition-colors duration-200',
          'shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.03)]',
          focused
            ? 'border-accent shadow-accentGlow'
            : invalid
              ? 'border-danger/60'
              : 'border-line hover:border-line-strong',
          disabled && 'opacity-60',
          className,
        )}
      >
        {/* Inline dot preview (hidden when reveal is on) */}
        {showDots && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-4 flex items-center gap-1.5 text-mono text-ink-muted"
          >
            {Array.from({ length: Math.min(charCount, 24) }).map((_, i) => (
              <motion.span
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ ...spring, delay: i * 0.012 }}
                className="inline-block size-1.5 rounded-full bg-ink-muted"
              />
            ))}
            {charCount > 24 && (
              <span className="ml-1 text-xs text-ink-faint">+{charCount - 24}</span>
            )}
          </div>
        )}

        <input
          ref={ref}
          type={revealed ? 'text' : 'password'}
          inputMode="text"
          autoComplete="current-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={disabled}
          value={value}
          className={cn(
            'peer w-full bg-transparent px-4 py-3.5 text-mono text-base tracking-wide',
            'text-ink placeholder:text-ink-faint focus:outline-none',
            'disabled:cursor-not-allowed',
            showDots && 'text-transparent caret-transparent',
          )}
          {...rest}
        />

        {/* Reveal toggle */}
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2 rounded p-2 text-ink-faint',
            'transition-colors hover:text-ink-muted',
          )}
        >
          {revealed ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3l18 18" />
              <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
              <path d="M16.7 16.7C15 17.6 13.5 18 12 18c-7 0-9-7-9-7a13.16 13.16 0 0 1 4.06-5.18" />
              <path d="M9.5 4.24A9.12 9.12 0 0 1 12 4c7 0 9 7 9 7a13.16 13.16 0 0 1-2.16 3.19" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';